/*
 * Copyright (c) GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import { Atom, PrimitiveAtom, WritableAtom, atom } from 'jotai';
import { activeSanctioningBodyIdAtom } from 'jotai/sanctioningBody';
import { atomFamily } from 'jotai/utils';
import { isEqual } from 'lodash';
import {
  DriverPointsData,
  ManufacturerPointsData,
  OriginType,
  OwnerPoints,
  SanctioningBodyEnum,
} from 'models/viewModels';
import { driverPointsRSocketService } from 'services/stream/RSocket/DriverPointsServiceRSocket';
import { manufacturerPointsRSocketService } from 'services/stream/RSocket/ManufacturerPointsServiceRSocket';
import { ownerPointsRSocketService } from 'services/stream/RSocket/OwnerPointsServiceRSocket';

const initManufacturerPointsAtomFamily = atomFamily<OriginType, WritableAtom<null, [], () => void>>(
  (dataSource: OriginType) => {
    const newAtom = atom<null, [], () => void>(null, (get, set) => {
      const sanctioningBody = get(activeSanctioningBodyIdAtom) ?? SanctioningBodyEnum.NASCAR;
      const listenerId = manufacturerPointsRSocketService
        .get(sanctioningBody)!
        .get(dataSource)!
        .addListener(
          async (data) => set(overwrittenManufacturerPointsAtomFamily(dataSource), data),
          async () => set(overwrittenManufacturerPointsAtomFamily(dataSource), [])
        );
      return () => manufacturerPointsRSocketService.get(sanctioningBody)!.get(dataSource)!.removeListener(listenerId);
    });
    newAtom.onMount = (init) => {
      init();
    };
    return newAtom;
  },
  (a, b) => isEqual(a, b)
);

const overwrittenManufacturerPointsAtomFamily = atomFamily<OriginType, PrimitiveAtom<ManufacturerPointsData[]>>(
  (originType: OriginType) => atom([] as ManufacturerPointsData[]),
  (a, b) => isEqual(a, b)
);

export const manufacturerPointsStreamAtomFamily = atomFamily<OriginType, Atom<ManufacturerPointsData[]>>(
  (dataSource: OriginType) =>
    atom<ManufacturerPointsData[]>((get) => {
      get(initManufacturerPointsAtomFamily(dataSource));
      return get(overwrittenManufacturerPointsAtomFamily(dataSource));
    }),
  (a, b) => isEqual(a, b)
);

const initDriverPointsAtomFamily = atomFamily<OriginType, WritableAtom<null, [], () => void>>(
  (dataSource: OriginType) => {
    const newAtom = atom<null, [], () => void>(null, (get, set) => {
      const sanctioningBody = get(activeSanctioningBodyIdAtom) ?? SanctioningBodyEnum.NASCAR;
      const listenerId = driverPointsRSocketService
        .get(sanctioningBody)!
        .get(dataSource)!
        .addListener(
          async (data) => set(overwrittenDriverPointsAtomFamily(dataSource), data),
          async () => set(overwrittenDriverPointsAtomFamily(dataSource), [])
        );
      return () => driverPointsRSocketService.get(sanctioningBody)!.get(dataSource)!.removeListener(listenerId);
    });
    newAtom.onMount = (init) => {
      init();
    };
    return newAtom;
  },
  (a, b) => isEqual(a, b)
);

const overwrittenDriverPointsAtomFamily = atomFamily<OriginType, PrimitiveAtom<DriverPointsData[]>>(
  (originType: OriginType) => atom([] as DriverPointsData[]),
  (a, b) => isEqual(a, b)
);

export const driverPointsStreamAtomFamily = atomFamily<OriginType, Atom<DriverPointsData[]>>(
  (dataSource: OriginType) =>
    atom<DriverPointsData[]>((get) => {
      get(initDriverPointsAtomFamily(dataSource));
      return get(overwrittenDriverPointsAtomFamily(dataSource));
    }),
  (a, b) => isEqual(a, b)
);

const initOwnerPointsAtomFamily = atomFamily<OriginType, WritableAtom<null, [], () => void>>(
  (dataSource: OriginType) => {
    const newAtom = atom<null, [], () => void>(null, (get, set) => {
      const sanctioningBody = get(activeSanctioningBodyIdAtom) ?? SanctioningBodyEnum.NASCAR;
      const listenerId = ownerPointsRSocketService
        .get(sanctioningBody)!
        .get(dataSource)!
        .addListener(
          async (data) => set(overwrittenOwnerPointsAtomFamily(dataSource), data),
          async () => set(overwrittenOwnerPointsAtomFamily(dataSource), [])
        );
      return () => ownerPointsRSocketService.get(sanctioningBody)!.get(dataSource)!.removeListener(listenerId);
    });
    newAtom.onMount = (init) => {
      init();
    };
    return newAtom;
  },
  (a, b) => isEqual(a, b)
);

const overwrittenOwnerPointsAtomFamily = atomFamily<OriginType, PrimitiveAtom<OwnerPoints[]>>(
  (originType: OriginType) => atom([] as OwnerPoints[]),
  (a, b) => isEqual(a, b)
);

export const ownerPointsStreamAtomFamily = atomFamily<OriginType, Atom<OwnerPoints[]>>(
  (dataSource: OriginType) =>
    atom<OwnerPoints[]>((get) => {
      get(initOwnerPointsAtomFamily(dataSource));
      return get(overwrittenOwnerPointsAtomFamily(dataSource));
    }),
  (a, b) => isEqual(a, b)
);
